import { ChevronDown, ChevronRight, PackageSearch, Pencil } from 'lucide-react';
import { Fragment, useMemo, useState, type ReactNode } from 'react';
import type { ScheduleItemView } from '@obraplan/core/application/contracts/schedule-items';
import { itemProgress } from '@obraplan/core/domain/takeoff/room-progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { cn } from '@/lib/cn';
import { formatBusinessDate } from '@/lib/format';
import { DEFAULT_BAR_COLOR, GanttAxis, GanttBar, GanttLegend, GanttSummaryBar } from './ItemGantt';
import {
  businessDaysBetween,
  formatDecimal,
  formatItemQuantity,
  formatShortRange,
  groupScheduleItems,
  impliedCrew,
  slipDays,
  timelineOf,
  type FloorGroup,
} from './schedule-items-model';

export interface ScheduleItemsTableProps {
  /** Os itens já filtrados, na ordem do servidor. */
  readonly items: readonly ScheduleItemView[];
  /** Sem ele, o botão de edição não existe (`schedule.edit`). */
  readonly onEdit?: (item: ScheduleItemView) => void;
  /** Sem ele, o botão de materiais e mão de obra do item não existe (Fase 26). */
  readonly onDetails?: (item: ScheduleItemView) => void;
  /** Hoje (`AAAA-MM-DD`), para a linha do Gantt e o sinal de "sem avanço". Sem ele, nenhum. */
  readonly today?: string;
  /** A cor de cada disciplina, pelo id: é a cor da barra. Sem ela, a da marca. */
  readonly colors?: ReadonlyMap<string, string>;
}

/** A largura da coluna da esquerda: pavimento, disciplina e serviço. */
const LEFT_COLUMN = 'w-[290px] xl:w-[380px]';
/** O recuo do detalhe e das faixas, alinhado ao nome do serviço na tela larga. */
const DETAIL_INDENT = 'pl-3 xl:pl-[123px]';

/**
 * O cronograma por mão de obra: torre → pavimento (pelo nível) → serviços, com o eixo do
 * tempo como protagonista (redesenho de 21/10/2026).
 *
 * Duas colunas: à esquerda, o pavimento, a disciplina e o serviço; à direita, o Gantt, dono
 * do resto da largura. O que antes eram colunas — datas, dias úteis, quantidade, HH, equipe,
 * avanço — vive no **detalhe da linha**, aberto ao clicar no serviço, junto com as ações:
 * editar o previsto e o avanço, e abrir a execução, os materiais e a mão de obra por função.
 *
 * Cada pavimento é uma faixa que recolhe, com a janela, as horas e a linha fina dele no
 * Gantt; a torre só aparece quando a obra tem mais de uma. A tabela rola por dentro, com o
 * eixo preso no topo.
 */
export function ScheduleItemsTable({
  items,
  onEdit,
  onDetails,
  today,
  colors,
}: ScheduleItemsTableProps) {
  const towers = useMemo(() => groupScheduleItems(items), [items]);
  const timeline = useMemo(() => timelineOf(items), [items]);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set());
  const [open, setOpen] = useState<string | undefined>(undefined);

  const toggleFloor = (floorId: string): void => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(floorId)) next.delete(floorId);
      else next.add(floorId);
      return next;
    });
  };

  const showTowers = towers.length > 1;

  return (
    <TableWrapper className="max-h-[75vh] overflow-y-auto">
      <Table className="min-w-[52rem] table-fixed">
        <colgroup>
          <col className={LEFT_COLUMN} />
          <col />
        </colgroup>
        <TableHeader sticky>
          <TableRow className="hover:bg-transparent">
            <TableHead className="h-auto px-0 pt-4 pb-1.5 align-bottom text-xs font-normal tracking-normal normal-case">
              Pavimento, disciplina e serviço
            </TableHead>
            <TableHead className="h-auto p-0 align-bottom">
              <span className="sr-only">Cronograma</span>
              {timeline !== undefined && (
                <GanttAxis timeline={timeline} today={today} legend={<GanttLegend />} />
              )}
            </TableHead>
          </TableRow>
        </TableHeader>

        {towers.map((tower) => (
          <TableBody key={tower.towerId}>
            {showTowers && (
              <GroupRow
                name={tower.towerName}
                meta={groupMeta(tower.itemCount, tower.start, tower.end, tower.laborHours)}
                tone="tower"
              >
                {timeline !== undefined && (
                  <GanttSummaryBar
                    name={tower.towerName}
                    summary={tower.summary}
                    timeline={timeline}
                    today={today}
                  />
                )}
              </GroupRow>
            )}

            {tower.floors.map((floor) => {
              const isCollapsed = collapsed.has(floor.floorId);
              const Icon = isCollapsed ? ChevronRight : ChevronDown;
              return (
                <Fragment key={floor.floorId}>
                  <GroupRow
                    name={floor.floorName}
                    meta={groupMeta(floor.items.length, floor.start, floor.end, floor.laborHours)}
                    tone="floor"
                    toggle={
                      <button
                        type="button"
                        aria-expanded={!isCollapsed}
                        onClick={() => toggleFloor(floor.floorId)}
                        className="flex min-h-9 w-full items-baseline gap-2.5 rounded-md text-left hover:text-(--color-brand)"
                      >
                        <Icon className="size-4 shrink-0 self-center" aria-hidden="true" />
                        <span className="font-semibold">{floor.floorName}</span>
                        <span className="font-condensed text-xs font-normal text-(--color-content-muted)">
                          {groupMeta(floor.items.length, floor.start, floor.end, floor.laborHours)}
                        </span>
                      </button>
                    }
                  >
                    {timeline !== undefined && (
                      <GanttSummaryBar
                        name={floor.floorName}
                        summary={floor.summary}
                        timeline={timeline}
                        today={today}
                      />
                    )}
                  </GroupRow>

                  {!isCollapsed &&
                    floor.items.map((item) => (
                      <ItemRows
                        key={item.id}
                        item={item}
                        floor={floor}
                        timeline={timeline}
                        today={today}
                        color={colors?.get(item.disciplineId) ?? DEFAULT_BAR_COLOR}
                        open={open === item.id}
                        onToggle={() =>
                          setOpen((current) => (current === item.id ? undefined : item.id))
                        }
                        {...(onEdit === undefined ? {} : { onEdit })}
                        {...(onDetails === undefined ? {} : { onDetails })}
                      />
                    ))}
                </Fragment>
              );
            })}
          </TableBody>
        ))}
      </Table>
    </TableWrapper>
  );
}

/** `22 serviços · 13/10–19/1 · 2.040 HH`. */
function groupMeta(count: number, start: string, end: string, hours: number): string {
  return `${String(count)} ${count === 1 ? 'serviço' : 'serviços'} · ${formatShortRange(start, end)} · ${formatDecimal(hours, 0)} HH`;
}

/** A faixa de um grupo: o nome e o resumo à esquerda, a linha fina à direita. */
function GroupRow({
  name,
  meta,
  tone,
  toggle,
  children,
}: {
  readonly name: string;
  readonly meta: string;
  readonly tone: 'tower' | 'floor';
  /** O botão de recolher, quando o grupo recolhe (o pavimento). */
  readonly toggle?: ReactNode;
  readonly children: ReactNode;
}) {
  return (
    <TableRow
      className={cn(
        'hover:bg-(--color-surface-muted)',
        tone === 'tower' ? 'bg-(--color-surface-muted)' : 'bg-(--color-surface-muted)/60',
      )}
    >
      <TableCell className={cn('py-1.5 pr-3 pl-0 align-middle', tone === 'floor' && 'pt-3')}>
        {toggle ?? (
          <span className="flex items-baseline gap-2.5">
            <span className="font-semibold">{name}</span>
            <span className="font-condensed text-xs text-(--color-content-muted)">{meta}</span>
          </span>
        )}
      </TableCell>
      <TableCell className={cn('p-0 align-middle', tone === 'floor' && 'pt-1.5')}>
        {children}
      </TableCell>
    </TableRow>
  );
}

/** A linha de um serviço: a barra à direita e, ao clicar, o detalhe com os números e as ações. */
function ItemRows({
  item,
  floor,
  timeline,
  today,
  color,
  open,
  onToggle,
  onEdit,
  onDetails,
}: {
  readonly item: ScheduleItemView;
  readonly floor: FloorGroup;
  readonly timeline: ReturnType<typeof timelineOf>;
  readonly today: string | undefined;
  readonly color: string;
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly onEdit?: (item: ScheduleItemView) => void;
  readonly onDetails?: (item: ScheduleItemView) => void;
}) {
  const label = `${item.disciplineName}${item.serviceName === null ? '' : ` · ${item.serviceName}`}${
    item.segment === null ? '' : ` · ${item.segment}`
  } · ${floor.floorName}`;

  return (
    <>
      <TableRow className={cn(open && 'border-b-0')}>
        <TableCell className="p-0 align-top">
          <button
            type="button"
            aria-expanded={open}
            onClick={onToggle}
            className="grid min-h-9 w-full grid-cols-[3px_minmax(0,1fr)] items-center gap-x-2.5 py-1.5 pr-3 text-left xl:grid-cols-[3px_110px_minmax(0,1fr)]"
          >
            <span
              aria-hidden="true"
              className="row-span-2 h-full min-h-7 self-stretch rounded-sm xl:row-span-1"
              style={{ background: color }}
            />
            <span className="font-condensed truncate text-[11px] text-(--color-content-muted) xl:text-xs">
              {item.disciplineName}
            </span>
            <span className="text-[13.5px] leading-tight font-medium">
              {item.serviceName ?? (
                <span className="font-normal text-(--color-content-muted) italic">
                  Frente inteira
                </span>
              )}
              {item.segment !== null && (
                <span className="font-condensed ml-1.5 text-xs font-normal text-(--color-content-muted)">
                  {item.segment}
                </span>
              )}
            </span>
          </button>
        </TableCell>
        <TableCell className="p-0 align-middle">
          {timeline !== undefined && (
            <GanttBar item={item} timeline={timeline} today={today} color={color} />
          )}
        </TableCell>
      </TableRow>
      {open && (
        <TableRow data-testid="detalhe-do-item">
          <TableCell colSpan={2} className={cn('pt-0 pr-3 pb-3', DETAIL_INDENT)}>
            <ItemDetail
              item={item}
              label={label}
              {...(onEdit === undefined ? {} : { onEdit })}
              {...(onDetails === undefined ? {} : { onDetails })}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

/**
 * O detalhe do serviço: o prazo, os dias úteis, a quantidade (com a origem e a marca de
 * provisória), o HH, a equipe e o avanço — e, ao lado, as ações da linha.
 */
function ItemDetail({
  item,
  label,
  onEdit,
  onDetails,
}: {
  readonly item: ScheduleItemView;
  readonly label: string;
  readonly onEdit?: (item: ScheduleItemView) => void;
  readonly onDetails?: (item: ScheduleItemView) => void;
}) {
  const days = businessDaysBetween(item.plannedStart, item.plannedEnd);
  // O item gerado do quantitativo tem a equipe que se informou (D5); o do arquivo, a que o prazo
  // sugere. Recalcular a gerada daria 6,5 pessoas onde se pediram 9 — o prazo arredonda o dia.
  const crew = item.crewSize ?? impliedCrew(item.laborHours, days);
  const quantity = formatItemQuantity(item);
  const slip = slipDays(item);
  const progress = itemProgress(item);
  // A origem do número (Fase 31, RN-418, RN-419): o selo e o número do arquivo, quando difere.
  const origin = item.takeoffMissing ? (
    <Badge
      tone="warn"
      className="whitespace-nowrap"
      title="O quantitativo não tem mais este pavimento × serviço: o item ficou, com o último número"
    >
      saiu do quantitativo
    </Badge>
  ) : item.quantitySource === 'QUANTITATIVO' ? (
    <Badge
      tone="info"
      className="whitespace-nowrap"
      title="A quantidade e o HH vêm do quantitativo da obra"
    >
      do quantitativo
    </Badge>
  ) : undefined;
  const fileQuantity =
    item.fileQuantity !== undefined && item.fileQuantity !== item.quantity
      ? (formatItemQuantity({ quantity: item.fileQuantity, unit: item.unit }) ?? undefined)
      : undefined;
  const fileHours =
    item.fileLaborHours !== undefined && item.fileLaborHours !== item.laborHours
      ? `${formatDecimal(item.fileLaborHours)} HH`
      : undefined;

  return (
    <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
      <dl className="font-condensed flex flex-wrap gap-x-5 gap-y-1.5 text-[12.5px] text-(--color-content-muted) [&_dd]:inline [&_dt]:sr-only">
        <div>
          <dt>Prazo</dt>
          <dd className="tabular">
            <span className="text-(--color-content)">{formatBusinessDate(item.plannedStart)}</span>{' '}
            a <span className="text-(--color-content)">{formatBusinessDate(item.plannedEnd)}</span>
            {slip > 0 && (
              <span
                className="ml-1.5 text-(--color-state-warn)"
                title="Dias corridos depois do fim da linha de base"
              >
                +{String(slip)} d sobre a linha de base
              </span>
            )}
          </dd>
        </div>
        <div>
          <dt>Dias úteis</dt>
          <dd className="tabular">
            {String(days)} {days === 1 ? 'dia útil' : 'dias úteis'}
          </dd>
        </div>
        <div>
          <dt>Quantidade</dt>
          <dd className="tabular">
            <span className="inline-flex flex-wrap items-center gap-1.5">
              {origin}
              {item.quantityProvisional && (
                <Badge
                  tone="warn"
                  title="Quantidade estimada: rateio por pavimento ou levantamento incompleto"
                >
                  provisória
                </Badge>
              )}
              {quantity ?? 'sem quantidade'}
            </span>
            {fileQuantity !== undefined && (
              <span
                className="ml-1.5"
                title="O número do arquivo, guardado quando o quantitativo assumiu o item"
              >
                (arquivo: {fileQuantity})
              </span>
            )}
          </dd>
        </div>
        <div>
          <dt>HH</dt>
          <dd className="tabular">
            {item.laborHours === null ? 'sem HH' : `${formatDecimal(item.laborHours)} HH`}
            {fileHours !== undefined && (
              <span
                className="ml-1.5"
                title="O HH do arquivo, guardado quando o quantitativo assumiu o item"
              >
                (arquivo: {fileHours})
              </span>
            )}
          </dd>
        </div>
        <div>
          <dt>Equipe</dt>
          <dd
            className="tabular"
            title={item.crewSize === undefined ? 'HH ÷ (dias úteis × 8)' : undefined}
          >
            {crew === null ? 'sem equipe' : `equipe de ${formatDecimal(crew, 1)}`}
          </dd>
        </div>
        <div>
          <dt>Avanço</dt>
          <dd className="tabular">
            <span className="inline-flex flex-wrap items-center gap-1.5">
              {progress.source === 'COMODOS' ? (
                <Badge
                  tone="ok"
                  title="Avanço pelos cômodos: a média dos percentuais apontados, pesada pela quantidade de cada cômodo"
                >
                  pelos cômodos
                </Badge>
              ) : progress.source === 'MEDICAO' ? (
                <Badge
                  tone="ok"
                  title={
                    item.executedQuantity === undefined
                      ? 'Avanço calculado pelo executado, e não pelo percentual digitado'
                      : `Executado: ${formatItemQuantity({ quantity: item.executedQuantity, unit: item.unit }) ?? ''} — o avanço sai do que foi executado, e não do percentual digitado`
                  }
                >
                  pela medição
                </Badge>
              ) : undefined}
              avanço {formatDecimal(progress.value, 1)}%
            </span>
          </dd>
        </div>
      </dl>
      {(onEdit !== undefined || onDetails !== undefined) && (
        <div className="flex gap-1">
          {onDetails !== undefined && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label={`Execução, materiais e mão de obra de ${label}`}
              title="Execução por cômodo ou peça, materiais e mão de obra por função"
              onClick={() => onDetails(item)}
            >
              <PackageSearch aria-hidden="true" />
              Execução e materiais
            </Button>
          )}
          {onEdit !== undefined && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label={`Editar ${label}`}
              onClick={() => onEdit(item)}
            >
              <Pencil aria-hidden="true" />
              Editar
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
